import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../../../store/reducers/rootReducer';
import ArrowIcon from '../../../../../generic/icons/IconLib/ArrowIcon';
import ChartLineIcon from '../../../../../generic/icons/IconLib/ChartLineIcon';
import PalletIcon from '../../../../../generic/icons/IconLib/PalletIcon';
import PhoneIcon from '../../../../../generic/icons/IconLib/PhoneIcon';
import PrintIcon from '../../../../../generic/icons/IconLib/PrintIcon';
import ScaleIcon from '../../../../../generic/icons/IconLib/ScaleIcon';
import TruckIcon from '../../../../../generic/icons/IconLib/TruckIcon';
import UserIcon from '../../../../../generic/icons/IconLib/UserIcon';
import { CardRow } from '../../../../../generic/ui/CardRow';
import { IWorkerItem } from '../../../../../modules/API/Defaults/models/workers';
import { IKavDataItem } from '../../../../../modules/API/Drivers/models/kavData';
import { aggregateCustomers } from '../helpers/aggregate';
import { actionsData } from '../helpers/kavAction';
import {
    ActionBtn,
    CardShell,
    DriversTag,
    DetachedChipStrip,
    IconSlot,
    MetricChip,
    NeutralBtn,
    OfficeActions,
    PrimaryName,
    RowShell,
    SecondaryText,
    TimeText,
} from './DriversCardParts';

const STATUS_ACTIVE_COLOR = '#F59E0B';
const STATUS_DONE_COLOR = '#22C55E';

const ACTION_START = 1;
const ACTION_FINISH = 2;

export type KavCardVariant = 'list' | 'header';

interface Props {
    item: IKavDataItem;
    isOffice: boolean;
    /** 'list' — строка списка кавов: кликабельна, с разделителем снизу. 'header' — шапка страницы кава. */
    variant: KavCardVariant;
    /** Только для 'list': последний в списке — без нижнего разделителя. */
    isLast?: boolean;
    /** Только для 'list': открыть страницу кава. */
    onOpen?: (areaID: number) => void;
    onAction: (kavID: number, action: number) => void;
    onPrintLog: (areaID: number) => void;
    onOpenStatistic: (item: IKavDataItem) => void;
}

const KavCard: React.FC<Props> = ({
    item,
    isOffice,
    variant,
    isLast = false,
    onOpen,
    onAction,
    onPrintLog,
    onOpenStatistic,
}) => {
    const workerList: IWorkerItem[] = useSelector(
        (state: RootState) => state.defaults.workerList,
    );

    const worker = useMemo(
        () => workerList.find((e) => e.WorkerId === item.DriverWorkerId),
        [workerList, item.DriverWorkerId],
    );

    const { weight, pallets } = useMemo(
        () => aggregateCustomers(item.Customers),
        [item.Customers],
    );

    const times = actionsData(item.Actions);
    const isFinished = times.Finish.isActive;
    const isStarted = times.Start.isActive;

    const isList = variant === 'list';
    const Shell = isList ? RowShell : CardShell;

    const stop = (e: React.MouseEvent) => e.stopPropagation();

    // В шапке строка некликабельна: на страницу кава ведёт только айтем списка.
    const rowProps = isList
        ? {
              $interactive: true,
              role: 'button',
              tabIndex: 0,
              onClick: () => onOpen?.(item.AreaID),
              onKeyDown: (e: React.KeyboardEvent) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onOpen?.(item.AreaID);
                  }
              },
          }
        : { $hover: false };

    return (
        <Shell {...(isList ? { $isLast: isLast } : {})}>
            <CardRow
                $columns='minmax(0, 1fr) auto auto'
                $mobileColumns='minmax(0, 1fr) auto auto'
                $isLast
                {...rowProps}
            >
                <CardRow.Cell>
                    <CardRow.Stack $gap='0.25rem'>
                        <CardRow.Line $gap='0.5rem'>
                            <IconSlot $muted>
                                <TruckIcon size={16} />
                            </IconSlot>
                            <PrimaryName>{item.AreaDesc?.trim()}</PrimaryName>
                            {isFinished ? (
                                <DriversTag variant='tinted' color={STATUS_DONE_COLOR}>
                                    הסתיים
                                </DriversTag>
                            ) : isStarted ? (
                                <DriversTag variant='tinted' color={STATUS_ACTIVE_COLOR}>
                                    בעבודה
                                </DriversTag>
                            ) : null}
                        </CardRow.Line>
                        {isOffice && (
                            <CardRow.Line $gap='0.375rem' $wrap>
                                <IconSlot $muted>
                                    <UserIcon size={14} />
                                </IconSlot>
                                <SecondaryText>{item.DriverName?.trim()}</SecondaryText>
                                {worker?.PhoneNum && (
                                    <>
                                        <IconSlot $muted>
                                            <PhoneIcon size={12} />
                                        </IconSlot>
                                        <TimeText>{worker.PhoneNum}</TimeText>
                                    </>
                                )}
                            </CardRow.Line>
                        )}
                    </CardRow.Stack>
                </CardRow.Cell>

                <CardRow.Cell $align='center'>
                    <CardRow.Stack $gap='0.125rem' $align='center'>
                        {isStarted && (
                            <CardRow.Line $gap='0.25rem'>
                                <IconSlot $muted>
                                    <ArrowIcon size={12} position='top' />
                                </IconSlot>
                                <TimeText>{times.Start.time}</TimeText>
                            </CardRow.Line>
                        )}
                        {isFinished && (
                            <CardRow.Line $gap='0.25rem'>
                                <IconSlot $muted>
                                    <ArrowIcon size={12} position='bottom' />
                                </IconSlot>
                                <TimeText>{times.Finish.time}</TimeText>
                            </CardRow.Line>
                        )}
                    </CardRow.Stack>
                </CardRow.Cell>

                <CardRow.Cell $align='center'>
                    {!isFinished && (
                        <ActionBtn
                            type='button'
                            onClick={(e) => {
                                stop(e);
                                onAction(item.AreaID, isStarted ? ACTION_FINISH : ACTION_START);
                            }}
                        >
                            {isStarted ? 'סיום' : 'התחלה'}
                        </ActionBtn>
                    )}
                </CardRow.Cell>
            </CardRow>

            <DetachedChipStrip onClick={stop}>
                <MetricChip icon={<UserIcon />} value={item.Customers.length} />
                <MetricChip icon={<PalletIcon />} value={pallets.toFixed(2)} />
                <MetricChip icon={<ScaleIcon />} value={Math.round(weight)} unit='ק"ג' />
                {isOffice && (
                    <OfficeActions>
                        <NeutralBtn
                            type='button'
                            onClick={() => onPrintLog(item.AreaID)}
                            aria-label='הדפסת יומן קו'
                            title='הדפסת יומן קו'
                        >
                            <PrintIcon />
                        </NeutralBtn>
                        <NeutralBtn
                            type='button'
                            onClick={() => onOpenStatistic(item)}
                            aria-label='סיכום אריזות'
                            title='סיכום אריזות'
                        >
                            <ChartLineIcon />
                        </NeutralBtn>
                    </OfficeActions>
                )}
            </DetachedChipStrip>
        </Shell>
    );
};

export default KavCard;
